import { get, isArray, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import keymirror from 'keymirror';

import { ruleMatchMap } from 'utils/filterAvailableLogicAnswers';
import blockLabels from 'config/blockLabels';
import isPageBreak from 'utils/isPageBreak';
import isScaleOrNumeric from 'utils/isScaleOrNumeric';
import numbering from 'sharedParts/utils/numbering';

const defaultLogicList = [{}];
const noLogicValue = 'no-logic';
const thankYouPageValue = 'thank-you-page';

export const logicFieldNames = keymirror({
  answerTriggerOption: null,
  logicAnswerValue: null,
  questionToJumpUuid: null,
});

const logicPropType = PropTypes.shape({
  logic: PropTypes.string,
  isEnabled: PropTypes.bool,
  logicList: PropTypes.arrayOf(
    PropTypes.shape({
      [logicFieldNames.answerTriggerOption]: PropTypes.arrayOf(PropTypes.string),
      [logicFieldNames.logicAnswerValue]: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      [logicFieldNames.questionToJumpUuid]: PropTypes.string,
    }),
  ),
  regardless: PropTypes.string,
});

const getQuestionLogicRawObject = (question) => get(question, 'logic', null);

const getQuestionLogicList = (question) =>
  get(getQuestionLogicRawObject(question), 'logicList', defaultLogicList);

const mapAnswerUuidToString = (answerUuidArray) =>
  isArray(answerUuidArray) && [...answerUuidArray].sort().join('');

export const getAnswerTriggerOption = (option) => get(option, logicFieldNames.answerTriggerOption, null);

export const getLogicAnswerValue = (option) => get(option, logicFieldNames.logicAnswerValue, null);

export const getQuestionToJumpUuid = (option) => get(option, logicFieldNames.questionToJumpUuid, null);

const isItemTypeMultiChoice = (logicItem, userAnswerUuid) =>
  isArray(getLogicAnswerValue(logicItem)) && isArray(userAnswerUuid);

const isItemTypeScale = (logicItem) => !!getAnswerTriggerOption(logicItem);

const isMultiChoiceItemEqualToAnswer = (logicItemAnswerUuid, userAnswerValue) => {
  if (logicItemAnswerUuid.length !== userAnswerValue.length) {
    return false;
  }

  return (
    mapAnswerUuidToString(logicItemAnswerUuid) === mapAnswerUuidToString(userAnswerValue.map(({ id }) => id))
  );
};

const isScaleItemEqualToAnswer = (logicItem, userAnswer) => {
  const answerTriggerValue = getLogicAnswerValue(logicItem);
  const answerTriggerOption = getAnswerTriggerOption(logicItem);
  const ruleFn = ruleMatchMap[answerTriggerOption];
  return ruleFn(userAnswer, answerTriggerValue);
};

const isLogicItemEqualToAnswer = (logicItem, userAnswerValue) => {
  const logicItemAnswerValue = getLogicAnswerValue(logicItem);

  if (isItemTypeMultiChoice(logicItem, userAnswerValue)) {
    return isMultiChoiceItemEqualToAnswer(logicItemAnswerValue, userAnswerValue);
  }

  if (isItemTypeScale(logicItem)) {
    return isScaleItemEqualToAnswer(logicItem, userAnswerValue);
  }

  return logicItemAnswerValue === userAnswerValue;
};

const getQuestionLogicListItem = (question, answerValue) =>
  getQuestionLogicList(question).find((logicItem) => isLogicItemEqualToAnswer(logicItem, answerValue)) ||
  null;

const doesLogicItemHaveOptionUuid = (logicItemAnswer, optionUuid) => {
  return isArray(logicItemAnswer) ? logicItemAnswer.includes(optionUuid) : logicItemAnswer === optionUuid;
};

const getQuestionLogicListItemIndices = (question, optionUuid) => {
  const logicList = getQuestionLogicList(question);
  return logicList
    .map(
      (logicItem, index) =>
        doesLogicItemHaveOptionUuid(getLogicAnswerValue(logicItem), optionUuid) ? index : null,
    )
    .filter((item) => item !== null);
};

const getQuestionRegardlessLogicUuid = (question) =>
  get(getQuestionLogicRawObject(question), 'regardless', null);

const isRegardlessSpecified = (question) => {
  const regardless = getQuestionRegardlessLogicUuid(question);

  return regardless !== noLogicValue && !!regardless;
};

const isQuestionLogicEnabled = (question) => !!get(getQuestionLogicRawObject(question), 'isEnabled', false);

const isLogicItemValueValid = (item) => {
  if (isArray(item)) {
    return !isEmpty(item);
  }

  return item === 0 || !!item;
};

const isLogicItemEmpty = (logicItem) => {
  if (getQuestionToJumpUuid(logicItem) === noLogicValue) {
    return false;
  }

  const defaultKeyCount = 2;
  const scaleKeyCount = 3;
  const nonEmptyKeyCount = isItemTypeScale(logicItem) ? scaleKeyCount : defaultKeyCount;
  const nonEmptyLogicItemValues = Object.values(logicItem).filter(isLogicItemValueValid);

  return nonEmptyLogicItemValues.length === nonEmptyKeyCount;
};

const isQuestionLogicListEmpty = (question) => {
  const logicList = getQuestionLogicList(question);
  const isQuestionListEmpty = logicList.length === 0;

  const doesQuestionListHaveNotEmptyItems = logicList.some(isLogicItemEmpty);
  return isQuestionListEmpty || !doesQuestionListHaveNotEmptyItems;
};

const isQuestionLogicSpecified = (question) =>
  !isQuestionLogicListEmpty(question) || isRegardlessSpecified(question);

const isQuestionLogicValid = (question) =>
  isQuestionLogicEnabled(question) && isQuestionLogicSpecified(question);

const isQuestionLogicShow = (question) =>
  isQuestionLogicValid(question) && question.logic && question.logic.action === 'show';

const isQuestionLogicSkip = (question) =>
  isQuestionLogicValid(question) && question.logic && question.logic.action === 'skip';

const generateSecondaryLabel = (order, type) => `[#${numbering.toHuman(order)} ${blockLabels[type]}]`;

const generateLabelWithQuestionType = (plainText, type, order) =>
  `${plainText} ${generateSecondaryLabel(order, type)}`;

const getLogicSuggestions = (questionSectionBlocks, currentQuestionUuid) => {
  const filteredBlocks = questionSectionBlocks
    .filter((block) => !isPageBreak(block))
    .map((props, index) => ({
      displayOrder: index,
      ...props,
    }))
    .filter((block) => block.clientUuid !== currentQuestionUuid)
    .map(({ clientUuid, plainText, type, displayOrder }) => ({
      value: clientUuid,
      label: generateLabelWithQuestionType(plainText, type, displayOrder),
      primaryLabel: plainText,
      secondaryLabel: generateSecondaryLabel(displayOrder, type),
    }));

  const noLogicBlock = {
    value: noLogicValue,
    label: 'no logic',
  };

  const thankYouPageBlock = {
    value: thankYouPageValue,
    label: 'Thank you page',
  };

  return [noLogicBlock, thankYouPageBlock, ...filteredBlocks];
};

const isQuestionUsedInList = (questionUuid, questionList) => {
  const blockListWithoutCurrentBlock = questionList.filter(
    (question) => question.clientUuid !== questionUuid,
  );
  return blockListWithoutCurrentBlock.some((question) => {
    const questionLogicList = getQuestionLogicList(question);
    const isInsideAnotherQuestionLogicList = questionLogicList.some((logicItem = {}) => {
      const questionToJumpUuid = getQuestionToJumpUuid(logicItem);
      return questionToJumpUuid === questionUuid;
    });
    return isInsideAnotherQuestionLogicList || getQuestionRegardlessLogicUuid(question) === questionUuid;
  });
};

const isOptionSelectedInLogic = (question, optionUuid) => {
  const logicList = getQuestionLogicList(question);
  return logicList.some((logicItem) =>
    doesLogicItemHaveOptionUuid(getLogicAnswerValue(logicItem), optionUuid),
  );
};

// taker stuff
const getQuestionLogicListItemRedirectBlockUuid = (question, answerValue) =>
  getQuestionToJumpUuid(getQuestionLogicListItem(question, answerValue));

const getQuestionLogicRedirectBlockUuid = (question, answer) => {
  if (!isQuestionLogicValid(question) || !answer) {
    return null;
  }

  const { value, id: answerId } = answer;

  if (isArray(value) && isEmpty(value)) {
    return null;
  }

  const isMultiChoice = isArray(value);

  if (isMultiChoice || isScaleOrNumeric(question)) {
    return (
      getQuestionLogicListItemRedirectBlockUuid(question, value) || getQuestionRegardlessLogicUuid(question)
    );
  }

  return (
    getQuestionLogicListItemRedirectBlockUuid(question, answerId) || getQuestionRegardlessLogicUuid(question)
  );
};

const getDefaultLogic = () => ({
  logicList: getQuestionLogicList(),
  isEnabled: isQuestionLogicEnabled(),
  regardless: getQuestionRegardlessLogicUuid(),
});

const getQuestionLogic = (question) => ({
  ...getQuestionLogicRawObject(question),
  logicList: getQuestionLogicList(question),
  isEnabled: isQuestionLogicEnabled(question),
  regardless: getQuestionRegardlessLogicUuid(question),
});

export const addConditionalLogicActionToBlock = (questionBlock) => {
  if (!!questionBlock.logic && !!questionBlock.logic.logicList) {
    return {
      ...questionBlock,
      logic: {
        ...questionBlock.logic,
        action: questionBlock.logic.logicList.some((item) => !!item.result) ? 'show' : 'skip',
        ...(questionBlock.logic && {
          logicList: questionBlock.logic.logicList.map((item) => {
            const object = {
              ...item,
              questionToJumpUuid: item.result ? item.result.client_uuid : item.questionToJumpUuid,
            };
            delete object.result;
            return object;
          }),
        }),
      },
    };
  }
  return questionBlock;
};

export default {
  logicFieldNames,
  defaultLogicList,
  thankYouPageValue,
  logicPropType,
  getQuestionLogic,
  addConditionalLogicActionToBlock,
  getQuestionLogicList,
  getQuestionLogicListItemIndices,
  isQuestionLogicValid,
  isQuestionLogicShow,
  isQuestionLogicSkip,
  isQuestionUsedInList,
  isOptionSelectedInLogic,
  getQuestionLogicRedirectBlockUuid,
  getLogicSuggestions,
  getDefaultLogic,
  mapAnswerUuidToString,
  getQuestionRegardlessLogicUuid,
  generateLabelWithQuestionType,
  generateSecondaryLabel,
};
